import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdOnboardingSteps } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import {
  COMPANY_ONBOARDING_STEP_KEYS,
  CompanyOnboardingStep,
  summarizeCompanyOnboardingProgress,
} from '~hooks/useCompanyOnboarding/models';
import connection from '~services/connectionModule';

import { COMPANY_ONBOARDING_STALE_TIME_IN_MS } from './constants';

const isKnownStep = (step: CompanyOnboardingStep) =>
  COMPANY_ONBOARDING_STEP_KEYS.includes(step.stepKey);

export const COMPANY_ONBOARDING_PROGRESS_QUERY_KEY = 'companyOnboardingProgress';

interface UseCompanyOnboardingProgressQueryProps {
  companyId: string;
  isEnabled?: boolean;
  notifiesOnError?: boolean;
  refetchIntervalMs?: number;
}

export default function useCompanyOnboardingProgressQuery({
  companyId,
  isEnabled = true,
  notifiesOnError = true,
  refetchIntervalMs,
}: UseCompanyOnboardingProgressQueryProps) {
  const {
    data: steps,
    dataUpdatedAt,
    isError,
    isPending,
  } = useQuery({
    queryKey: [COMPANY_ONBOARDING_PROGRESS_QUERY_KEY, companyId],
    queryFn:
      isEnabled && companyId
        ? async ({ signal }) => {
            try {
              const response = await getV1CompaniesCompanyIdOnboardingSteps({
                path: { 'company-id': companyId },
                // A step has at most one live completion, so this one page holds them all.
                query: { 'page[limit]': MAX_API_PAGE_SIZE },
                signal,
              });

              return response.data.data
                .map(CompanyOnboardingStep.parse)
                .filter(isKnownStep);
            } catch (error) {
              if (!notifiesOnError) {
                throw error;
              }

              connection.handleRequestError(
                error,
                t('error_messages.company_onboarding.failed_to_fetch'),
              );
            }
          }
        : skipToken,
    refetchInterval: ({ state }) =>
      state.data?.length === COMPANY_ONBOARDING_STEP_KEYS.length
        ? false
        : refetchIntervalMs,
    // handleRequestError already toasts, so retries would repeat the same toast.
    retry: false,
    staleTime: COMPANY_ONBOARDING_STALE_TIME_IN_MS,
  });

  const completedStepsByKey = useMemo(
    () => new Map(steps?.map((step) => [step.stepKey, step])),
    [steps],
  );

  return {
    ...summarizeCompanyOnboardingProgress(completedStepsByKey),
    completedStepsByKey,
    dataUpdatedAt,
    hasLoaded: Boolean(steps),
    isError,
    isPending,
  };
}
